'use client';

/**
 * Every layer of a v2 order as a tile, from the first second (the order plan
 * writes all layer rows up front): waiting, running with the step it is on,
 * ready, partial, failed, cancelled, or waiting for a button — «Нарисовать
 * персонажей» / «Нарисовать страницы», each an add-on order (§15.1).
 */
import { useTranslations } from 'next-intl';
import {
  AlertTriangle, BookOpen, CheckCircle2, Circle, Clock, FileText, Headphones,
  Loader2, Printer, Upload, Video, XCircle, FileDown,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import type { LayerKind, LayerState } from '../contracts';
import type { AddOnPart, OrderLayersView } from '../order/types';

type Layer = OrderLayersView['layers'][number];

const KIND_ICON: Record<LayerKind, typeof FileText> = {
  story: FileText,
  illustration: BookOpen,
  narration: Headphones,
  pdf: FileDown,
  print: Printer,
  video: Video,
  youtube: Upload,
};

function StateIcon({ state }: { state: LayerState }) {
  switch (state) {
    case 'running': return <Loader2 className="size-4 shrink-0 animate-spin text-primary" />;
    case 'ready': return <CheckCircle2 className="size-4 shrink-0 text-success" />;
    case 'partial': return <AlertTriangle className="size-4 shrink-0 text-warning" />;
    case 'failed': return <XCircle className="size-4 shrink-0 text-destructive" />;
    case 'cancelled': return <XCircle className="size-4 shrink-0 text-muted-foreground" />;
    case 'awaiting_user': return <Clock className="size-4 shrink-0 text-primary" />;
    default: return <Circle className="size-4 shrink-0 text-muted-foreground/40" />;
  }
}

export interface OrderLayerTileProps {
  layer: Layer;
  /** the add-on order behind the button; absent → the button is shown disabled */
  onAddOn?: Partial<Record<AddOnPart, () => void>>;
  /** «Выбрать другой голос»: the narration waits on its voice */
  onChooseVoice?: (layer: Layer) => void;
  /** a button press in flight */
  pending?: boolean;
}

/** The step label of a layer: running, or waiting on the parent / a failure point. */
export function stepLabelKey(layer: Pick<Layer, 'state' | 'step'>): string | null {
  if (!layer.step) return null;
  if (layer.state === 'running' || layer.state === 'awaiting_user' || layer.state === 'partial' || layer.state === 'failed') return `steps.${layer.step}`;
  return null;
}

export function OrderLayerTile({ layer, onAddOn, onChooseVoice, pending }: OrderLayerTileProps) {
  const t = useTranslations('stories.orderLayers');
  const Icon = KIND_ICON[layer.kind];
  const waitsVoice = layer.kind === 'narration' && layer.state === 'awaiting_user';
  const awaiting = layer.kind === 'illustration' && layer.state === 'awaiting_user'
    ? ((layer.params.awaiting as AddOnPart | null | undefined) ?? 'characters')
    : null;
  const stepKey = stepLabelKey(layer);
  const stepLabel = stepKey
    ? (typeof t.has === 'function' && t.has(stepKey) ? t(stepKey) : layer.state === 'running' ? t('steps.default') : null)
    : null;
  const handler = awaiting ? onAddOn?.[awaiting] : undefined;

  return (
    <div
      className="flex min-w-[10rem] flex-1 items-start gap-2 rounded-md border bg-card p-3"
      data-testid={`order-layer-${layer.kind}`}
      data-state={layer.state}
    >
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="text-sm font-medium">{t(`kind.${layer.kind}`)}</div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`order-layer-${layer.kind}-state`}>
          <StateIcon state={layer.state} />
          <span>{t(`state.${layer.state}`)}</span>
        </div>
        {stepLabel && (
          <div className="truncate text-xs text-muted-foreground" data-testid={`order-layer-${layer.kind}-step`}>
            {stepLabel}
          </div>
        )}
        {awaiting && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-1 h-7 text-xs"
            disabled={!handler || pending}
            onClick={handler}
            data-testid={`order-layer-${layer.kind}-addon-${awaiting}`}
          >
            {t(awaiting === 'characters' ? 'drawCharacters' : 'drawPages')}
          </Button>
        )}
        {waitsVoice && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-1 h-7 text-xs"
            disabled={!onChooseVoice || pending}
            onClick={() => onChooseVoice?.(layer)}
            data-testid={`order-layer-${layer.kind}-choose-voice`}
          >
            {t('chooseVoice')}
          </Button>
        )}
      </div>
    </div>
  );
}

export interface OrderLayerTilesProps {
  order: OrderLayersView | null | undefined;
  onAddOn?: Partial<Record<AddOnPart, () => void>>;
  onChooseVoice?: (layer: Layer) => void;
  pending?: boolean;
}

export function OrderLayerTiles({ order, onAddOn, onChooseVoice, pending }: OrderLayerTilesProps) {
  const t = useTranslations('stories.orderLayers');
  if (!order || order.layers.length === 0) return null;
  return (
    <section className="space-y-2" data-testid="order-layers" data-order-status={order.status}>
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('title')}</h2>
      <div className="flex flex-wrap gap-2">
        {order.layers.map((l) => <OrderLayerTile key={l.layerId} layer={l} onAddOn={onAddOn} onChooseVoice={onChooseVoice} pending={pending} />)}
      </div>
    </section>
  );
}
